import type {
  ExternalCourse,
  ExternalSubject,
  PublicFormVerificationStatus,
  SubjectRequest,
  WorkExperience,
} from './api'

export type ExternalCourseRowClass = {
  InvalidExternalCourse: boolean
}

export type ExternalSubjectRowClass = {
  ValidExternalSubject: boolean
  InvalidExternalSubject: boolean
}

export type WorkExperienceRowClass = {
  InvalidExternalExperience: boolean
}

export function filterPreviousLearningExternalCourses(
  externalCourses: ExternalCourse[],
  studentCourseId: string | number | null | undefined,
): ExternalCourse[] {
  if (studentCourseId == null) return externalCourses

  return externalCourses.filter((course) => {
    return course.student_course_id == null || Number(course.student_course_id) !== Number(studentCourseId)
  })
}

export function externalCourseHasSubjects(course: ExternalCourse): boolean {
  return course.external_course_level_has_subjects !== false
}

export function externalCourseRequiresArticulationForCredit(course: ExternalCourse): boolean {
  return course.external_course_level_credit_requires_articulation === true
}

export function externalCourseAllowsLtuSubjectRequests(course: ExternalCourse): boolean {
  return course.external_course_level_allow_request_ltu_subject === true
    && !externalCourseRequiresArticulationForCredit(course)
}

export function studentRequestedCourseSubjectRequests(course: ExternalCourse): SubjectRequest[] {
  if (!externalCourseAllowsLtuSubjectRequests(course)) return []

  return course.subject_request_list.filter((request) => request.student_requested === true)
}

export function articulationAutoAddedCourseSubjectRequests(course: ExternalCourse): SubjectRequest[] {
  return course.subject_request_list.filter((request) => request.articulation_auto_added === true)
}

export function externalCourseCreditMessage(course: ExternalCourse): string {
  if (externalCourseRequiresArticulationForCredit(course)) {
    return course.articulation_agreements.length > 0
      ? 'Matching articulation agreement found - no need to add subjects in the next step.'
      : 'Not eligible for credit.'
  }

  return externalCourseHasSubjects(course)
    ? 'Add subject in next step.'
    : 'Not eligible for credit.'
}

export function filterExternalCoursesWithSubjects(externalCourses: ExternalCourse[]): ExternalCourse[] {
  return externalCourses.filter(externalCourseHasSubjects)
}

export function isVerifiedStatus(status: PublicFormVerificationStatus): boolean {
  return status === 2
}

export function isInvalidVerificationStatus(status: PublicFormVerificationStatus): boolean {
  return status === -1 || status === 1
}

export function externalCourseRowClass(course: ExternalCourse): ExternalCourseRowClass {
  return {
    InvalidExternalCourse: isInvalidVerificationStatus(course.external_course_verified),
  }
}

export function externalSubjectRowClass(subject: ExternalSubject): ExternalSubjectRowClass {
  return {
    ValidExternalSubject: isVerifiedStatus(subject.external_subject_verified),
    InvalidExternalSubject: isInvalidVerificationStatus(subject.external_subject_verified),
  }
}

export function workExperienceRowClass(workExperience: WorkExperience): WorkExperienceRowClass {
  return {
    InvalidExternalExperience: isInvalidVerificationStatus(workExperience.experience_verified),
  }
}

export function canEditExternalCourse(course: ExternalCourse): boolean {
  return !isVerifiedStatus(course.external_course_verified)
}

export function canEditExternalSubject(subject: ExternalSubject): boolean {
  return !isVerifiedStatus(subject.external_subject_verified)
}

export function canEditWorkExperience(workExperience: WorkExperience): boolean {
  return !isVerifiedStatus(workExperience.experience_verified)
}

export function canAddExternalSubject(course: ExternalCourse): boolean {
  return course.external_course_system_managed !== true
}

export function emptyExternalSubjectsMessage(course: ExternalCourse): string {
  return course.external_course_system_managed
    ? '== No completed subjects =='
    : '== No completed subjects, click below to add some =='
}

export function externalCourseSubjectRowspan(course: ExternalCourse): number {
  if (!externalCourseAllowsLtuSubjectRequests(course)) return 1

  return Math.max(1, studentRequestedCourseSubjectRequests(course).length)
}

export function workExperienceSubjectRowspan(workExperience: WorkExperience): number {
  return Math.max(1, workExperience.subject_request_list.length)
}

export function workExperienceRequiresRequestedSubject(workExperience: WorkExperience): boolean {
  const requestedSubjectCount = workExperience.subject_request_list?.length ?? 0
  return requestedSubjectCount === 0
    && (workExperience.experience_verified === 0 || workExperience.experience_verified === 1)
}

export function hasWorkExperienceMissingRequestedSubject(workExperiences: WorkExperience[]): boolean {
  return workExperiences.some(workExperienceRequiresRequestedSubject)
}
